import * as React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { Button, Colors, Display } from '../Components/Button/Button';
import { action, observable, runInAction } from 'mobx';
import { TextInput } from 'Views/Components/New/TextInput';
import { store } from 'Models/Store';
import { isEmail } from 'Validators/Functions/Email';
import alert from '../../Util/ToastifyUtils';
import { getErrorMessages } from 'Util/GraphQLUtils';
import { resetPasswordRequest } from 'Services/Api/AccountService';
import { parse as parseQueryString } from 'querystring';
import If from 'Views/Components/If/If';
import { ACTIVATE_NEW_PASSWORD_PAGE_TITLE, FORGOT_PASSWORD_PAGE_TITLE } from 'ConstantPageNames';
import { themeStore } from 'Models/ThemeStore';
import { useEffect } from 'react';
import Card from 'Views/Components/_HumanWritten/Card/Card';
import { Flex } from 'Views/Components/Flex/Flex';
import useDeviceDetect from 'Hooks/useDeviceDetect';

interface IResetRequestState {
	username: string;
	errors: { [attr: string]: string };
	resetEmailSent: boolean;
	loading: boolean;
}

const defaultResetRequestState: IResetRequestState = {
	username: '',
	errors: {},
	resetEmailSent: false,
	loading: false,
};

const ResetPasswordRequestPage = observer(() => {
	const location = useLocation();
	const navigate = useNavigate();
	const { isMobile } = useDeviceDetect();

	const [resetRequestState] = React.useState(() => observable(defaultResetRequestState));
	const [forcedReset, setForcedReset] = React.useState(false);

	// Both kinds of customer land here holding a password they have never seen, and both are sent
	// the same way: forcedReset. They differ only in why, which is what the wording has to get
	// right — a customer whose account staff created has no old system to be told their details
	// were carried across from. Set by the login step off the account's own record; absent for
	// anyone who reached this page any other way, which correctly reads as not-migrated.
	const [migratedUser, setMigratedUser] = React.useState(false);

	useEffect(() => {
		const params = parseQueryString(location.search.substring(1));

		if (typeof params.email === 'string') {
			runInAction(() => {
				resetRequestState.username = params.email as string;
			});
		}

		if (typeof params.forcedReset === 'string' && params.forcedReset === 'true') {
			setForcedReset(true);
		}

		setMigratedUser(typeof params.migratedUser === 'string' && params.migratedUser === 'true');
	}, [location.search]);

	const onResetPasswordClicked = action((event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		resetRequestState.errors = {};

		if (!resetRequestState.username) {
			resetRequestState.errors.username = 'This field is required';
		} else if (!isEmail(resetRequestState.username)) {
			resetRequestState.errors.username = 'Invalid email address';
		}

		if (Object.keys(resetRequestState.errors).length === 0) {
			runInAction(() => {
				resetRequestState.loading = true;
			});

			resetPasswordRequest(resetRequestState.username)
				.then(() => {
					onResetPasswordSent();
				})
				.catch(response => {
					const errorMessages = getErrorMessages(response).map((error: any) => <p>{error.message}</p>);
					alert(
						<div>
							<h6>Could not send request</h6>
							{errorMessages}
						</div>,
						'error',
					);
				})
				.finally(() => {
					runInAction(() => {
						resetRequestState.loading = false;
					});
				});
		}
	});

	const onResetPasswordSent = action(() => {
		resetRequestState.resetEmailSent = true;
	});

	if (store.loggedIn) {
		return <Navigate to="/" replace />;
	}

	useEffect(() => {
		if (forcedReset) {
			document.title = ACTIVATE_NEW_PASSWORD_PAGE_TITLE;
		} else {
			document.title = FORGOT_PASSWORD_PAGE_TITLE;
		}
	}, []);

	return (
		<div className="login-page">
			<Link className="logo-link" to="/home">
				<img
					src={isMobile ? themeStore.logoSecondaryLightURL : themeStore.logoPrimaryLightURL}
					alt="Company Logo"
				/>
			</Link>
			<div
				className="top-section"
				style={{
					backgroundImage: `url(${themeStore.loginBackgroundImageURL})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
				}}
			/>
			<div className="bottom-section" />
			<div className="center-section">
				<div className="login-flow">
					<form onSubmit={e => onResetPasswordClicked(e)}>
						<Card>
							<If condition={!forcedReset}>
								<h5>Forgot password?</h5>
								<p className="body--content">
									<FormattedMessage
										id="password-reset__label"
										defaultMessage="It happens! We'll send a link to the email below to reset your password and you can be on your way to paradise again."
									/>
								</p>
							</If>
							<If condition={forcedReset && migratedUser}>
								<h5>
									<FormattedMessage
										id="new-system__header"
										defaultMessage="We have upgraded our system!"
									/>
								</h5>
								<p className="body--content">
									<FormattedMessage
										id="new-system__body"
										defaultMessage="Your details and bookings will still be available, we just had to reset your password for security reasons. Follow the steps to re-activate your account."
									/>
								</p>
							</If>
							<If condition={forcedReset && !migratedUser}>
								<h5>
									<FormattedMessage
										id="activate-account__header"
										defaultMessage="Activate your account"
									/>
								</h5>
								<p className="body--content">
									<FormattedMessage
										id="activate-account__body"
										defaultMessage="Your account was set up for you, so you'll need to choose a password before you can log in. We'll send an activation link to the email below."
									/>
								</p>
							</If>
							<TextInput
								id="username"
								className="username"
								label="Email address"
								autoComplete="username"
								placeholder="Email address"
								required
								value={resetRequestState.username ?? ''}
								error={resetRequestState.errors.username}
								onChange={e =>
									runInAction(() => {
										resetRequestState.username = e.target.value;
										resetRequestState.resetEmailSent = false;
									})
								}
							/>
							<Flex direction="col" gap="xs">
								{resetRequestState.resetEmailSent && (
									<p>
										<span style={{ fontWeight: 600 }}>Email sent!</span> Check your inbox.
									</p>
								)}
								<Button
									type="submit"
									display={Display.Solid}
									colors={Colors.Primary}
									newStyles
									disabled={resetRequestState.username.length <= 0 || resetRequestState.loading}
								>
									{forcedReset
										? // eslint-disable-next-line no-nested-ternary
											`${
												resetRequestState.loading
													? 'Sending...'
													: resetRequestState.resetEmailSent
														? 'Resend activation email'
														: 'Send activation email'
											}`
										: // eslint-disable-next-line no-nested-ternary
											`${
												resetRequestState.loading
													? 'Sending...'
													: resetRequestState.resetEmailSent
														? 'Resend reset email'
														: 'Send reset email'
											}`}
								</Button>
								<Button
									display={Display.Outline}
									colors={Colors.Secondary}
									newStyles
									onClick={() => navigate('/login')}
								>
									Back to login
								</Button>
							</Flex>
						</Card>
					</form>
				</div>
			</div>
		</div>
	);
});

export default ResetPasswordRequestPage;
